// ============================================
// Path: /frontend/src/pages/CarDealersPage.jsx
// Purpose: Display all registered car dealers and yards with subscription tiers
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { AgentCard, AgentCardSkeleton } from '../components/AgentCard';
import { usersAPI } from '../api/users.api';
import { User, Search, ArrowUpDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// ========================================
// MAIN COMPONENT
// ========================================
export default function CarDealersPage() {
  console.log('🎨 [CarDealersPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('random');

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchDealers = async () => {
      console.log('🔵 [CarDealersPage] Fetching car dealers with subscription tiers...');
      setLoading(true);
      
      try {
        const data = await usersAPI.getAllDealers({
          subscriptionTier: 'BRONZE,SILVER,GOLD,PLATINUM'
        });
        console.log('✅ [CarDealersPage] Dealers data received:', data);
        
        // Filter out dealers with no listings
        const activeDealers = (data.users || []).filter(dealer => 
          dealer._count && dealer._count.listings > 0
        );
        console.log(`🔍 [CarDealersPage] Filtered to ${activeDealers.length} dealers with listings (from ${data.users?.length || 0} total)`);
        
        setDealers(activeDealers);
        setError(null);
      } catch (err) {  // ✅ Add this catch block
        console.error('❌ [CarDealersPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load car dealers');
      } finally {
        setLoading(false);
      }
    };

    fetchDealers();
  }, []);

    // ========================================
    // FILTERED AND SORTED DEALERS
    // ========================================
    const filteredAndSortedDealers = useMemo(() => {
      let filtered = dealers;

      // Apply search filter
      if (searchQuery.trim()) {
        filtered = filtered.filter(dealer => 
          dealer.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          dealer.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Apply sorting
      let sorted = [...filtered];
      switch (sortOption) {
        case 'name-asc':
          sorted.sort((a, b) => a.fullName.localeCompare(b.fullName));
          break;
        case 'name-desc':
          sorted.sort((a, b) => b.fullName.localeCompare(a.fullName));
          break;
        case 'random':
        default:
          // Keep original order (already filtered)
          break;
      }

      return sorted;
    }, [dealers, searchQuery, sortOption]);

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* Page Header - Standard Pattern */}
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
            Car Dealers
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            Browse all of our registered car dealers and yards.
          </p>
        </div>

        {/* Error State */}
        {error && (
          <p className="text-destructive text-center text-sm sm:text-base py-4 px-4">
            Error: {error}
          </p>
        )}

        {/* Search and Sort Controls */}
        {!loading && dealers.length > 0 && (
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search car yards by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-xs sm:text-sm h-9 sm:h-10"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="w-full sm:w-48">
              <Select value={sortOption} onValueChange={setSortOption}>
                <SelectTrigger className="text-xs sm:text-sm h-9 sm:h-10">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random" className="text-xs sm:text-sm">Random Order</SelectItem>
                  <SelectItem value="name-asc" className="text-xs sm:text-sm">Name (A-Z)</SelectItem>
                  <SelectItem value="name-desc" className="text-xs sm:text-sm">Name (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}


        {/* Dealers Grid - 2 columns mobile, 4 desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
          {loading && 
            Array.from({ length: 12 }).map((_, i) => (
              <AgentCardSkeleton key={i} />
            ))
          }
          {!loading && filteredAndSortedDealers.map((dealer) => (
            <AgentCard
              key={dealer.id}
              agentId={dealer.id}
              name={dealer.fullName}
              email={dealer.email}
              isCarYard={true}
            />
          ))}
        </div>

        {/* No Search Results State */}
        {!loading && filteredAndSortedDealers.length === 0 && searchQuery && (
          <div className="text-center py-8 sm:py-12 text-muted-foreground">
            <Search className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" />
            <p className="text-sm sm:text-base">No car yards found matching "{searchQuery}"</p>
            <button 
              onClick={() => setSearchQuery('')}
              className="text-xs sm:text-sm text-primary hover:underline mt-2"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && dealers.length === 0 && !error && (
          <div className="text-center py-8 sm:py-12 md:py-16 text-muted-foreground px-4">
            <User className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto mb-2 sm:mb-3 md:mb-4" />
            <p className="text-sm sm:text-base">No car dealers have registered yet.</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}